import { Navigate, Outlet, useLocation, useParams } from "react-router-dom";
import { useSession } from "@/frontend/lib/auth";
import { usePostHog } from "posthog-js/react";
import { useEffect, useRef } from "react";
import { getBrowserTimezone } from "@/frontend/lib/date-utils";
import { hc } from "@/frontend/lib/hc";
import {
  captureProductEvent,
  clearPendingAuthEvent,
  getAuthEventFromCallbackUrl,
  getUrlWithoutAuthEvent,
  rememberMarketingHandoff,
  takePendingAuthEvent,
} from "@/frontend/lib/product-analytics";

export const ProtectedRoute = () => {
  const { data: session, isPending } = useSession();
  const location = useLocation();
  const posthog = usePostHog();
  const { orgId } = useParams<{ orgId?: string }>();
  const timezoneUpdatedRef = useRef(false);

  // Identify user in PostHog when session is available
  useEffect(() => {
    if (session?.user) {
      posthog.identify(session.user.id, {
        email: session.user.email,
        name: session.user.name,
      });
      const callbackAuthEvent = getAuthEventFromCallbackUrl(window.location.href);
      const pendingAuthEvent = callbackAuthEvent ?? takePendingAuthEvent();
      if (pendingAuthEvent) {
        if (pendingAuthEvent.properties.handoff_id) {
          rememberMarketingHandoff(pendingAuthEvent.properties.handoff_id);
        }
        captureProductEvent(posthog, pendingAuthEvent);
      }
      if (callbackAuthEvent) {
        clearPendingAuthEvent();
        const cleanedUrl = getUrlWithoutAuthEvent(window.location.href);
        window.history.replaceState(window.history.state, "", cleanedUrl);
      }
    }
  }, [session, posthog]);

  // Auto-update timezone when session is available and we have an orgId
  useEffect(() => {
    if (session?.user?.emailVerified === true && orgId && !timezoneUpdatedRef.current) {
      const timezone = getBrowserTimezone();
      timezoneUpdatedRef.current = true;

      // Update timezone in background (don't block rendering)
      hc.api.settings.organizations[":orgId"]["user-settings"]
        .$patch({
          param: { orgId },
          json: { timezone },
        })
        .catch((error) => {
          // Silently ignore 404 errors (organization doesn't exist or user doesn't have access)
          // This can happen when a user deleted their account and the org was deleted
          if (error?.response?.status !== 404) {
            console.error("Failed to auto-update timezone:", error);
          }
          // Reset ref on error so it can retry later
          timezoneUpdatedRef.current = false;
        });
    }
  }, [session, orgId]);

  if (isPending) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!session) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (session.user.emailVerified !== true) {
    return <Navigate to="/login?verify_email=1" state={{ from: location }} replace />;
  }

  return <Outlet />;
};
